import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
  computed,
  input,
  model,
  signal,
} from '@angular/core';
import { timer } from 'rxjs';
import { TreeTab } from '@core/models/tab-list.model';
import { CommonModule } from '@angular/common';
import { HorizontalScrollDirective } from '@shared/directives/horizontal-scroll.directive';
import { RoundedIconButtonComponent } from '../rounded-icon-button/rounded-icon-button.component';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { CallComponentMethodPipe } from '@shared/pipes/call-component-method/call-component-method.pipe';
import { ScrollToIdDirective } from '@shared/directives/scroll-to-id.directive';
import { tabListAtomResolveConfig } from '@front-platform/content-builder';
import type { TabListAtomConfig } from '@front-platform/content-builder';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { BlockConfigWithCssColors } from '@shared/utils/content-builder/resolve-block-config-colors';

export type TabListAtomConfigInput = Partial<
  TabListAtomConfig | BlockConfigWithCssColors<TabListAtomConfig>
> &
  Record<string, unknown>;

interface TreeTabUI extends TreeTab {
  isActive: boolean;
}

@Component({
  selector: 'app-tab-list-atom',
  templateUrl: './tab-list-atom.component.html',
  styleUrls: ['./tab-list-atom.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    HorizontalScrollDirective,
    RoundedIconButtonComponent,
    ContentBuilderColorPipe,
    CallComponentMethodPipe,
    ScrollToIdDirective,
  ],
})
export class TabListAtomComponent {
  readonly activeItemId = model<string | undefined>(undefined);
  readonly search = input<boolean>();
  readonly treeTabs = input.required<TreeTab[]>();
  readonly isNotched = input<boolean>();
  readonly config = input<TabListAtomConfigInput>({});

  readonly resolvedConfig = computedResolvedConfig(tabListAtomResolveConfig, () => ({
    config: this.config() as Partial<TabListAtomConfig>,
  }));

  protected readonly categoriesColorsByLevel = computed<
    Record<
      number,
      {
        backgroundColor: string;
        activeColor: string;
        inactiveColor: string;
      }
    >
  >(() => {
    const config = this.resolvedConfig();

    return {
      0: {
        backgroundColor: config.categories_background_color,
        activeColor: config.categories_active_color,
        inactiveColor: config.categories_inactive_color,
      },
      1: config.secondary_categories_colors,
      2: config.third_categories_colors,
    };
  });

  @Output() tabChange = new EventEmitter<{
    tabId: string;
  }>();
  @Output() searchEvent = new EventEmitter<Event>();
  @Output() isBurgerNavigationOpen = new EventEmitter<boolean>();

  @ViewChild('scrollSection') scrollSectionElement?: ElementRef;
  @ViewChild('btnMenu') btnMenu?: ElementRef;

  protected readonly activeMenu = signal(false);

  protected readonly treeTabsUI = computed(() => {
    return this.configureActiveTab({
      activeTabId: this.activeItemId(),
      treeTab: this.treeTabs(),
    });
  });

  private configureActiveTab(
    {
      activeTabId,
      treeTab,
    }: {
      activeTabId: string | undefined;
      treeTab: TreeTab[];
    },
    { isParentActive }: { isParentActive: boolean } = { isParentActive: false },
  ): TreeTabUI[] {
    const levelContainsActiveId =
      !!activeTabId && treeTab.some(tab => this.containsTabId(tab, activeTabId));

    const result = treeTab.map((tab, index) => {
      const isThisTabActive =
        activeTabId === tab.id ||
        (!activeTabId && index === 0) ||
        (isParentActive && index === 0 && !levelContainsActiveId);

      const children = tab.children
        ? this.configureActiveTab(
            { activeTabId, treeTab: tab.children },
            {
              isParentActive: isThisTabActive,
            },
          )
        : [];

      const hasActiveChild = children.some(child => child.isActive);
      const isActive = isThisTabActive || (hasActiveChild && !!activeTabId);

      return {
        ...tab,
        isActive,
        children,
      } satisfies TreeTabUI;
    });
    return result;
  }

  protected tabClick(tabId: string): void {
    this.activeItemId.set(tabId);
    this.tabChange.emit({ tabId });
    this.highlightSelectedItem();
    if (this.activeMenu) {
      this.activeMenu.set(false);
    }
  }

  private highlightSelectedItem(): void {
    timer(1).subscribe(() => {
      const allScrollTargetItems = document.querySelectorAll('.scroll-target-item');
      const currentChild = allScrollTargetItems[allScrollTargetItems.length - 1] as HTMLElement;
      if (this.scrollSectionElement && currentChild) {
        const scrollSection = this.scrollSectionElement.nativeElement;
        const offset = this.btnMenu?.nativeElement.offsetWidth ?? 0;
        scrollSection.scrollTo({
          left: this.resolvedConfig().display_burger_navigation
            ? currentChild.offsetLeft - offset
            : currentChild.offsetLeft + (currentChild.offsetWidth - scrollSection.offsetWidth) / 2,
          behavior: 'smooth',
        });
      }
    });
  }

  toggleSearch(): void {
    this.searchEvent.emit();
  }

  toggleMenu(): void {
    this.activeMenu.set(!this.activeMenu());
    this.isBurgerNavigationOpen.emit(this.activeMenu());
  }

  public typedTplDataEntry = (data: TreeTabUI[], level: number) => ({
    data,
    level,
  });
  public typedTplData = (data: object) =>
    data as {
      data: TreeTabUI[];
      level: number;
    };

  protected getActiveTab = (tabs: TreeTabUI[]): TreeTabUI | undefined => {
    const activeTabId = this.activeItemId();
    if (activeTabId) {
      const exact = tabs.find(tab => tab.id === activeTabId);
      if (exact) {
        return exact;
      }
      const containing = tabs.find(tab => this.containsTabId(tab, activeTabId));
      if (containing) {
        return containing;
      }
    }
    return tabs.find(tab => tab.isActive) ?? tabs[0];
  };

  private containsTabId(tab: TreeTab, activeTabId: string): boolean {
    if (tab.id === activeTabId) {
      return true;
    }
    return (tab.children ?? []).some(child => this.containsTabId(child, activeTabId));
  }
}
